// Main app content (instruments, indicators, charts, backtest). Shown at /app when authenticated.
import { useMemo } from "react";
import { Link } from "react-router-dom";
import InstrumentSelector from "./components/InstrumentSelector";
import IndicatorSelector from "./components/IndicatorSelector";
import Backtest from "./components/Backtest";
import BacktestCardActions from "./components/BacktestCardActions";
import BacktestResults from "./components/BacktestResults";
import BacktestTradeList from "./components/BacktestTradeList";
import PrimarySeriesChart from "./components/PrimarySeriesChart";
import AdditionalInstrumentChart from "./components/AdditionalInstrumentChart";
import EquityCurveChart from "./components/EquityCurveChart";
import TimeInMarketChart from "./components/TimeInMarketChart";
import IndicatorChart from "./components/IndicatorChart";
import { getChartBehavior } from "./utils/indicatorDisplayRegistry";
import { useAppStore } from "./state/store";
import { useFetchOhlcWhenNeeded } from "./hooks/useFetchOhlcWhenNeeded";
import { useFetchIndicatorSeriesWhenNeeded } from "./hooks/useFetchIndicatorSeriesWhenNeeded";
import type { Indicator } from "./state/types";
import Card from "./components/Card";
import { useAuth } from "./auth/AuthContext";

export default function AppMain() {
  const { signOut } = useAuth();
  useFetchOhlcWhenNeeded();
  useFetchIndicatorSeriesWhenNeeded();

  const backtestSummary = useAppStore((state) => state.backtestSummary);
  const backtestLoading = useAppStore((state) => state.backtestLoading);
  const currentSavedBacktestName = useAppStore((state) => state.currentSavedBacktestName);
  const ohlcLoading = useAppStore((state) => state.ohlcLoading);
  const ohlcError = useAppStore((state) => state.ohlcError);
  const setOhlcError = useAppStore((state) => state.setOhlcError);
  const instruments = useAppStore((state) => state.instruments);
  const primaryInstrumentId = useAppStore((state) => state.primaryInstrumentId);
  const ohlc = useAppStore((state) => state.ohlc);
  const additionalInstruments = instruments
    .filter((i) => i.id !== primaryInstrumentId)
    .filter((inst) => ohlc?.[inst.id] && Object.keys(ohlc[inst.id].series ?? {}).length > 0);

  const indicators = useAppStore((state) => state.indicators);
  const indicatorSeries = useAppStore((state) => state.indicatorSeries) ?? {};
  const indicatorsWithOwnChart = useMemo(
    () =>
      indicators.filter(
        (ind) => getChartBehavior(ind.type) === "ownChart" && indicatorSeries[ind.id]
      ),
    [indicators, indicatorSeries]
  );

  const handleIndicatorAdd = (indicator: Indicator): string => {
    return useAppStore.getState().addIndicator(indicator);
  };

  return (
    <div className="min-h-screen bg-newsprint text-ink">
      <header className="bg-newsprint-panel border-b border-rule p-6">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-3xl font-semibold text-ink mb-0 font-headline shrink-0">CountingTrades.com</h1>
            <Link
              to="/app/tracking"
              className="text-sm underline hover:no-underline text-ink/80 shrink-0"
            >
              Trade tracking
            </Link>
            {currentSavedBacktestName && (
              <>
                <span className="text-ink/50 shrink-0" aria-hidden>|</span>
                <span className="text-sm text-ink/70 font-headline truncate" title={currentSavedBacktestName}>
                  Loaded: {currentSavedBacktestName}
                </span>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={signOut}
            className="text-sm underline hover:no-underline text-ink/80"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 py-8 space-y-8">
        <section>
          <Card>
            <h2 className="text-xl font-semibold mb-4 font-headline">Instrument Selector</h2>
            <InstrumentSelector />
            {ohlcLoading && (
              <div
                className="flex flex-col items-center justify-center py-6 text-ink/80"
                role="status"
                aria-live="polite"
              >
                <span
                  className="inline-block h-6 w-6 border-2 border-accent border-t-transparent rounded-full animate-spin mb-2"
                  aria-hidden
                />
                <p className="text-sm">Loading instrument data…</p>
              </div>
            )}
            {ohlcError && (
              <div
                className="mt-4 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300"
                role="alert"
              >
                <p className="text-sm font-medium">Could not load data</p>
                <p className="text-sm mt-1">{ohlcError}</p>
                <button
                  type="button"
                  onClick={() => setOhlcError(null)}
                  className="mt-2 text-xs underline hover:no-underline"
                >
                  Dismiss
                </button>
              </div>
            )}
          </Card>
        </section>

        <section>
          <Card>
            <h2 className="text-xl font-semibold mb-4 font-headline">Indicators</h2>
            <IndicatorSelector onIndicatorAdd={handleIndicatorAdd} />
          </Card>
        </section>

        <section>
          <Card>
            <h2 className="text-xl font-semibold mb-4 font-headline">Primary Series</h2>
            <PrimarySeriesChart />
          </Card>
        </section>

        {indicatorsWithOwnChart.map((ind) => (
          <section key={ind.id}>
            <Card>
              <IndicatorChart indicatorId={ind.id} />
            </Card>
          </section>
        ))}

        {additionalInstruments.map((inst) => (
          <section key={inst.id}>
            <Card>
              <h2 className="text-xl font-semibold mb-4 font-headline">{inst.symbol}</h2>
              <AdditionalInstrumentChart instrumentId={inst.id} symbol={inst.symbol} />
            </Card>
          </section>
        ))}

        <section>
          <Card>
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <h2 className="text-xl font-semibold font-headline shrink-0">Backtest</h2>
                {currentSavedBacktestName && (
                  <>
                    <span className="text-ink/50 shrink-0" aria-hidden>·</span>
                    <span className="text-sm text-ink/70 font-headline truncate" title={currentSavedBacktestName}>
                      {currentSavedBacktestName}
                    </span>
                  </>
                )}
              </div>
              <BacktestCardActions />
            </div>
            <Backtest />
          </Card>
        </section>

        {(backtestSummary || backtestLoading) && (
          <>
            {backtestLoading ? (
              <section>
                <Card>
                  <div
                    className="flex flex-col items-center justify-center py-12 text-ink/80"
                    role="status"
                    aria-live="polite"
                  >
                    <span
                      className="inline-block h-8 w-8 border-2 border-accent border-t-transparent rounded-full animate-spin mb-3"
                      aria-hidden
                    />
                    <p>Running backtest…</p>
                  </div>
                </Card>
              </section>
            ) : backtestSummary ? (
              <>
                <section>
                  <Card>
                    <EquityCurveChart />
                  </Card>
                </section>

                <section>
                  <Card>
                    <TimeInMarketChart />
                  </Card>
                </section>

                <section>
                  <Card>
                    <BacktestResults summary={backtestSummary} />
                  </Card>
                </section>

                <section>
                  <Card>
                    <h2 className="text-xl font-semibold mb-4 font-headline">Backtest Trades</h2>
                    <BacktestTradeList />
                  </Card>
                </section>
              </>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
